<template>
  <MainLayout content-class="v-suppliers-content" fix-height>
    <LayoutContentHeader>
      <template #actions>
        <ElButton type="primary" @click="onCreateButtonClick"
          >+ {{ $t('Supplier.AddSupplier') }}
        </ElButton>
      </template>
    </LayoutContentHeader>
    <SuppliersTable
      :loading="loading"
      v-model:page="page.value"
      v-model:per-page="perPage.value"
      :items="items"
      :total="total"
      @modal:open="onEditButtonClick" />
  </MainLayout>
</template>

<script lang="ts">
export default {
  name: 'VSuppliers',
  inheritAttrs: false,
  customOptions: {},
};
</script>

<script lang="ts" setup>
import axios from 'axios';
import { useStore } from 'vuex';
import { computed, ref, watch } from 'vue';
import { ElNotification } from 'element-plus/es';

import { usePerPage, usePage } from '~shared/lib';
import { I18nService } from '~shared/lib';
import { compareQueriesThenLoadData } from '@/utils/router.utils';
import { Supplier } from '@/models/warehouse/Supplier.model';
import { SupplierDto } from '@/types/api';
import { MainLayout } from '~widgets/layouts';
import LayoutContentHeader from '@/components/layouts/assets/LayoutContentHeader/index.vue';
import SuppliersTable from '@/components/warehouse/tables/SuppliersTable/index.vue';
import SupplierCrudModal from '@/components/warehouse/modals/SupplierCrudModal/index.vue';

const store = useStore();
const loading = ref(false);
const perPage = usePerPage();
const page = usePage();

const total = computed(() => store.state.suppliers.total);
const items = computed(() => store.state.suppliers.data);
const query = computed(() => ({
  per_page: perPage.value,
  page: page.value,
}));

const getSuppliers = async () => {
  loading.value = true;
  try {
    const { data } = await Supplier.getItems(query.value);
    store.dispatch('suppliers/setData', {
      items: data.data,
      total: +data.meta.total,
      overwriteDataState: true,
    });
  } catch (err) {
    ElNotification({
      type: 'error',
      title: axios.isAxiosError(err) ? err.message : I18nService.t('Notifications.Error'),
    });
  } finally {
    loading.value = false;
  }
};

watch(
  query,
  (value, oldValue) => {
    compareQueriesThenLoadData({
      query: value,
      oldQuery: oldValue,
      getData: getSuppliers,
      resetPage: page.reset,
    });
  },
  { deep: true, immediate: true }
);

const onCreateButtonClick = async () => {
  await store.dispatch('modalAndDrawer/openModal', SupplierCrudModal);
};

const onEditButtonClick = async (payload: SupplierDto) => {
  await store.dispatch('modalAndDrawer/openModal', {
    component: SupplierCrudModal,
    payload: {
      formData: payload,
    },
  });
};
</script>

<style lang="scss"></style>
<i18n src="@/locales/base.locales.json" />
<i18n src="@/locales/warehouse.locales.json" />
